/* eslint-disable @typescript-eslint/ban-types */
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import 'date-fns'
import { useForm } from 'react-hook-form'
import moment from 'moment-timezone'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControl,
} from '@mui/material'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DatePicker from '@mui/lab/DatePicker'
import { Check as CheckIcon, Clear as ClearIcon, AddCircle as AddCircleIcon } from '@mui/icons-material'
import { fetchUsersListByGroupId, editUserData, fetchUserProfileData, fetchUsersData } from '../../actions'
import { RootState } from '../../reducers'
import { useStyles } from './UserProfileStyles'
import SnackBarAlert from '../HOC/SnackBarAlert'
import { IFormInputs, SentForm, UserQc, UserLicense, UserLicenseCopy } from 'interfaces/user'

export function EditUserAccount(props): JSX.Element {
  const user = props?.user
  const classes = useStyles()
  const dispatch = useDispatch()
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(new Date())
  const [userLicense, setuserLicense] = useState<UserLicense[]>([{ state_code: '', expiry_date: '' }])
  const [userQcSettings, setUserQcSettings] = useState<UserQc[]>([{ procedure_code_id: '', qc_percentage: '' }])
  const [processCodes, setProcessCodes] = useState<string[]>([])
  const [statesVariable, setStates] = useState<string[]>([])
  const [appConfig, setAppConfig] = useState([])
  const [userGroupValue, setUserGroupValue] = useState(null)
  const [userIdValue, setUserIdValue] = useState('')
  const [displayContactInformation, setDisplayContactInformation] = useState(false)
  const [userNames, setUserNames] = useState<string[]>([])
  const [firstName, setFirstName] = useState(null)
  const [credentials, setCredentials] = useState(null)
  const [lastName, setLastName] = useState(null)
  const [phoneNumber, setPhoneNumber] = useState(null)
  const [emailAddress, setEmailAddress] = useState(null)
  const [stateCount, setStateCount] = useState(1)
  const [qcCount, setQcCount] = useState(1)
  const [isShow, setIsShow] = useState(true)
  const [stateOverride, setStateOveride] = useState(false)
  const [performQc, setPerformQc] = useState(false)
  const [priorityCases, setProrityCases] = useState(false)
  const [medicareCases, setMedicareCases] = useState(false)
  const [multipleClassifiers, setMultipleclassifiers] = useState(false)
  const { data } = useSelector((state: RootState) => state.getUsersListByGroupId)
  const { ad_groups, procedure_codes, states, roles, max_case_load } = useSelector(
    (state: RootState) => state.appConfig,
  )
  const [displayStateError, setDisplayStateError] = useState(false)
  const { error, success } = useSelector((state: RootState) => state.editUser)
  const [displayProcessCodeError, setDisplayProcessCodeError] = useState(false)
  const [displayQcCodeError, setDisplayQcCodeError] = useState(false)
  const [requiredFieldsChanged, setRequiredFieldsChanged] = useState(false)
  const [open, setOpen] = React.useState(false)
  const [isSnack, setIsSnack] = useState(false)
  const [isDuplicatePc, setIsDuplicatePc] = useState(false)
  const refinedStates = states.map((value) => value.state_code)

  const handleClose = () => {
    setOpen(false)
  }

  const onCancel = (e) => {
    if (requiredFieldsChanged) {
      setOpen(true)
    } else {
      props.handleClose(e)
    }
  }

  const onConfirm = (e) => {
    if (displayStateError) {
      dispatch(fetchUsersData())
    }
    props.handleClose(e)
  }

  useEffect(() => {
    dispatch(fetchUserProfileData(user.id))
  }, [])

  const handleStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredFieldsChanged(true)
    setStateOveride(event.target.checked)
    setValue('any_state_override', stateOverride)
  }

  const handlePerformQc = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredFieldsChanged(true)
    setPerformQc(event.target.checked)
    setValue('priority_allowed', performQc)
  }

  const handlePriorityCases = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredFieldsChanged(true)
    setProrityCases(event.target.checked)
    setValue('priority_allowed', priorityCases)
  }

  const handleMedicareCases = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredFieldsChanged(true)
    setMedicareCases(event.target.checked)
    setValue('mc_allowed', medicareCases)
  }

  const handleMultipleclassifiers = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredFieldsChanged(true)
    setMultipleclassifiers(event.target.checked)
    setValue('multiple_classifiers_allowed', multipleClassifiers)
  }

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IFormInputs>({
    mode: 'onChange',
  })

  const addStateComponent = () => {
    setRequiredFieldsChanged(true)
    setStateCount(stateCount + 1)
    const tempuserLicense = [...userLicense]
    const stateObj: UserLicenseCopy = {
      state_code: '',
      expiry_date: `${moment().add(2, 'months').format('YYYY-MM-DD')}`,
    }
    tempuserLicense.push(stateObj)
    setuserLicense(tempuserLicense)
  }

  const addQcComponent = () => {
    setRequiredFieldsChanged(true)
    setQcCount(qcCount + 1)
    const tempuserQcSettings = [...userQcSettings]
    const qcObj: UserQc = {
      procedure_code_id: '',
      qc_percentage: '',
    }
    tempuserQcSettings.push(qcObj)
    setUserQcSettings(tempuserQcSettings)
  }

  const removeQcComponent = (e, i) => {
    setRequiredFieldsChanged(true)
    const tempQcSettings = [...userQcSettings]
    tempQcSettings.splice(i, 1)
    setUserQcSettings(tempQcSettings)
    const procedureCodeArray = tempQcSettings.map((value) => value.procedure_code_id)
    if (procedureCodeArray.length !== new Set(procedureCodeArray).size) {
      setIsDuplicatePc(true)
    } else {
      setIsDuplicatePc(false)
    }
    setQcCount(qcCount - 1)
  }

  const onSubmit = async (data: IFormInputs) => {
    const obj: SentForm = {
      user_id: data.user_id,
      user_roles: data.user_roles,
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data?.phone_number ? data.phone_number?.toString() : '',
      user_type: 'internal',
      user_status: true,
      credential: data.credential,
      email_id: data.email_id,
      qc_allowed: data.qc_allowed,
      user_licenses: userLicense,
      user_qc_settings: userQcSettings,
      priority_allowed: data.priority_allowed,
      mc_allowed: data.mc_allowed,
      multiple_classifiers_allowed: data.multiple_classifiers_allowed,
      max_cases_per_day: data.max_cases_per_day,
      any_state_override: data.any_state_override,
      user_ad_id: data?.email_id,
      lab_os_user_code: data?.lab_os_user_code,
      lab_os_login_id: data?.lab_os_login_id,
    }

    if (!isShow) {
      delete obj.user_licenses
      delete obj.user_qc_settings
    }

    await dispatch(editUserData({ payload: obj, id: user?.user_id }))

    if (error) {
      setIsSnack(true)
    } else {
      setTimeout(function () {
        props.setEditUserOpen(false)
      }, 700)
      setIsSnack(true)
    }
  }

  const handleDateChange = (date, index) => {
    setRequiredFieldsChanged(true)
    setSelectedDate(date)
    const dateStr = moment.tz(date, moment.locale()).format('YYYY-MM-DD').toString()
    setValue('expiry_date', dateStr)
    const state = [...userLicense]
    state[index].expiry_date = dateStr
    setuserLicense(state)
  }

  const procedureCode = procedure_codes.map((value) => {
    return value.cdx_procedure_code + ' - ' + value.description
  })

  useEffect(() => {
    setAppConfig(adRoles)
    setProcessCodes(procedure_codes)
  }, [ad_groups, data, dispatch, procedure_codes, states, user?.user_id])

  // on componet mount user data append
  useEffect(() => {
    setUserGroupValue(user?.user_roles[0])
    dispatch(fetchUsersListByGroupId({ groupId: user?.user_roles[0] }))
    setUserIdValue(user?.last_name + ', ' + user?.first_name)
    setDisplayContactInformation(true)
    setValue('user_id', user.user_id)
    setValue('user_roles', user.user_roles)
    setValue('first_name', user.first_name)
    setValue('last_name', user.last_name)
    setValue('email_id', user.email_id)
    setValue('phone_number', user.phone_number)
    setValue('credential', user.credential)
    setValue('max_cases_per_day', user?.max_cases_per_day)
    setValue('any_state_override', stateOverride)
    setValue('qc_allowed', user.qc_allowed)
    setValue('priority_allowed', user.priority_allowed)
    setValue('mc_allowed', user.mc_allowed)
    setValue('multiple_classifiers_allowed', user.multiple_classifiers_allowed)
    setValue('lab_os_user_code', user.lab_os_user_code)
    setValue('lab_os_login_id', user.lab_os_login_id)
    if (user?.user_licenses) {
      setuserLicense(user.user_licenses)
    }
    const getSelectedState = user?.user_licenses && user?.user_licenses.map((value) => value.state_code)
    const filteredStates = refinedStates.filter(function (obj) {
      return getSelectedState && getSelectedState.indexOf(obj) == -1
    })
    setStates(filteredStates)
    if (user?.user_qc_settings) setUserQcSettings(user?.user_qc_settings)
    if (user?.any_state_override) setStateOveride(true)
    if (user?.qc_allowed) setPerformQc(true)
    if (user?.priority_allowed) setProrityCases(true)
    if (user?.mc_allowed) setMedicareCases(true)
    if (user?.multiple_classifiers_allowed) setMultipleclassifiers(true)
    if (user?.user_licenses?.length > 1) setStateCount(user?.user_licenses?.length)
    if (user?.user_qc_settings?.length > 1) setQcCount(user?.user_qc_settings?.length)
  }, [
    dispatch,
    setValue,
    user.credential,
    user.email_id,
    user.first_name,
    user.last_name,
    user?.max_cases_per_day,
    user.mc_allowed,
    user.multiple_classifiers_allowed,
    user.phone_number,
    user.priority_allowed,
    user.qc_allowed,
    user.user_id,
    user.user_licenses,
    user?.user_qc_settings,
    user?.user_roles,
    user?.lab_os_user_code,
    user?.lab_os_login_id,
  ])

  useEffect(() => {
    const show = userGroupValue === 'NL_Pathologist' ? true : false
    setIsShow(show)
  }, [userGroupValue])

  const adRoles = roles.map((value) => value.name)

  const onCodeChange = (val, index) => {
    setRequiredFieldsChanged(true)
    const filterProcedureCodes = procedure_codes.filter((value) => {
      return value.cdx_procedure_code == val?.substring(0, 3).trim()
    })
    if (val === null) {
      setDisplayProcessCodeError(true)
    } else {
      setValue('procedure_code_id', filterProcedureCodes[0]?.id)
      const processCode = [...userQcSettings]
      processCode[index].procedure_code_id = filterProcedureCodes[0].id
      setUserQcSettings(processCode)
      const procedureCodeArray = userQcSettings.map((value) => value.procedure_code_id)
      if (procedureCodeArray.length !== new Set(procedureCodeArray).size) {
        setIsDuplicatePc(true)
      } else {
        setIsDuplicatePc(false)
      }
      setDisplayProcessCodeError(false)
    }
  }

  const dataProcedurecode = (val) => {
    const filterProcedureCodes = procedure_codes.filter((value) => {
      return value.id == val
    })
    if (val == '') {
      return ''
    } else {
      return filterProcedureCodes[0]?.cdx_procedure_code + ' - ' + filterProcedureCodes[0]?.description
    }
  }

  const onQcChange = (val: string, index: number) => {
    setRequiredFieldsChanged(true)
    setValue('qc_percentage', val)
    setDisplayQcCodeError(false)
    const qcPercentage = [...userQcSettings]
    qcPercentage[index].qc_percentage = val
    setUserQcSettings(qcPercentage)
  }

  const onStateChange = (val, index) => {
    setRequiredFieldsChanged(true)
    const state = [...userLicense]
    state[index].state_code = val
    setuserLicense(state)
    const filteredState = statesVariable.filter((word) => word !== val)
    setStates(filteredState)
    if (userLicense[index].state_code == null) {
      setDisplayStateError(true)
    } else return setDisplayStateError(false)
  }

  const removeStateComponent = (e, i) => {
    setRequiredFieldsChanged(true)
    const tempuserLicense = [...userLicense]
    tempuserLicense.splice(i, 1)
    setuserLicense(tempuserLicense)
    setStateCount(stateCount - 1)
    const addSelectedState = tempuserLicense.map((value) => value.state_code)
    const filteredStates = refinedStates.filter(function (obj) {
      return addSelectedState && addSelectedState.indexOf(obj) == -1
    })
    setStates(filteredStates)
  }

  const disableButton =
    displayStateError ||
    isDuplicatePc ||
    userLicense[0].state_code === '' ||
    userQcSettings[0].procedure_code_id === '' ||
    displayProcessCodeError
      ? true
      : false

  return (
    <React.Fragment>
      <div>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          classes={{ paper: classes.discardDialogHeight }}
        >
          <DialogTitle className={classes.dialogTitle}>Discard Changes</DialogTitle>
          <DialogContent className={classes.discardDialogContent}>
            <DialogContentText id="alert-dialog-description">
              You have pending unsaved changes. Do you really want to discard them?
            </DialogContentText>
          </DialogContent>
          <DialogActions className={classes.buttonActions}>
            <Button
              className={`${classes.btn} ${classes.buttonMargin}`}
              onClick={onConfirm}
              autoFocus
              endIcon={<CheckIcon />}
            >
              Confirm
            </Button>
            <Button
              className={`${classes.buttonMargin}`}
              onClick={handleClose}
              variant="outlined"
              endIcon={<ClearIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <Dialog
        open={props.editUserOpen}
        className={classes.modalWidth}
        maxWidth={'md'}
        scroll={'paper'}
        fullWidth
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <DialogTitle id="scroll-dialog-title" className={classes.dialogTitle}>
          Edit User Profile
        </DialogTitle>
        {isSnack && (
          <SnackBarAlert
            severity={error ? 'error' : 'success'}
            message={error ? 'Failed to update the user' : 'User updated successfully'}
          />
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers={true}>
            <DialogContentText id="scroll-dialog-description" tabIndex={-1}>
              <Box sx={{ flexGrow: 1 }} className={classes.modalViewHeight}>
                {displayContactInformation && (
                  <div>
                    <h3 className={classes.lableViewMrg10}>Contact Information</h3>
                    <Grid container spacing={2} className={classes.mT10}>
                      <Grid item xs={4}>
                        <TextField
                          inputProps={{
                            style: {
                              padding: 10,
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          id="outlined-basic"
                          label="First Name"
                          value={firstName}
                          disabled
                          {...register('first_name')}
                        />
                        {errors.state_code && <p className="errors">{errors.state_code.message}</p>}
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          inputProps={{
                            style: {
                              padding: 10,
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          id="outlined-basic"
                          label="Last Name"
                          disabled
                          value={lastName}
                          {...register('last_name')}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          inputProps={{
                            style: {
                              padding: 10,
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          id="outlined-basic"
                          label="Credentials (ex. M.D.)"
                          disabled
                          value={credentials}
                          {...register('credential')}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} className={classes.mT10}>
                      <Grid item xs={4}>
                        <TextField
                          inputProps={{
                            style: {
                              padding: 10,
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          id="outlined-basic"
                          label="Email Address"
                          disabled
                          value={emailAddress}
                          {...register('email_id')}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          inputProps={{
                            style: {
                              padding: 10,
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          id="outlined-basic"
                          value={phoneNumber}
                          disabled={displayContactInformation}
                          label="Phone"
                          {...register('phone_number')}
                        />
                      </Grid>
                    </Grid>
                    {isShow && (
                      <>
                        <Grid container spacing={2}>
                          <Grid item xs={8}>
                            <h3 className={classes.lableViewMrg}>State Licenses</h3>
                          </Grid>
                          <Grid item xs={4} className={classes.mT10}>
                            <Button color="primary" onClick={addStateComponent} endIcon={<AddCircleIcon />}>
                              Add
                            </Button>
                          </Grid>
                        </Grid>
                        {userLicense.map((val, i) => (
                          // eslint-disable-next-line react/jsx-key
                          <Grid key={i} container spacing={2} className={classes.mT10}>
                            <Grid item xs={4}>
                              <Autocomplete
                                id="combo-box-demo"
                                key={`refinedStates${i}`}
                                options={statesVariable}
                                value={val?.state_code || ''}
                                onChange={(_e, val) => onStateChange(val, i)}
                                renderInput={(params) => (
                                  <TextField {...params} required label="State/Locale" size="small" defaultValue="" />
                                )}
                              />
                              {displayStateError && !val?.state_code && (
                                <p className={classes.error}>Please select the State with Licenses</p>
                              )}
                            </Grid>
                            <Grid item xs={4} className={classes.mpTB0}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  key={`dates${i}`}
                                  label="Expires"
                                  value={val.expiry_date || moment()}
                                  onChange={(newValue) => handleDateChange(newValue, i)}
                                  InputProps={{
                                    style: {
                                      height: 40,
                                    },
                                  }}
                                  renderInput={(params) => <TextField {...params} />}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={4}>
                              <Button
                                color="primary"
                                key={`remove${i}`}
                                onClick={(e) => removeStateComponent(e, i)}
                                disabled={stateCount === 1}
                                endIcon={<ClearIcon />}
                              >
                                Remove
                              </Button>
                            </Grid>
                          </Grid>
                        ))}
                        <Grid container spacing={2} className={classes.mT0}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox checked={stateOverride} onChange={handleStateChange} color="primary" />
                              }
                              {...register('any_state_override')}
                              label="User can perform state overides"
                              labelPlacement="end"
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                    {isShow && (
                      <>
                        <Grid container spacing={2}>
                          <Grid item xs={8}>
                            <h3 className={classes.lableViewMrg}>Caseload Settings</h3>
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} className={classes.mT10}>
                          <Grid item xs={4}>
                            <TextField
                              className={classes.customLableTxt}
                              style={{ width: '100%' }}
                              InputLabelProps={{
                                style: { border: '1px solid red !important' },
                                shrink: true,
                              }}
                              inputProps={{
                                style: {
                                  padding: 10,
                                },
                                min: 1,
                                max: max_case_load,
                              }}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault()
                                }
                              }}
                              id="outlined-basic"
                              label="Max cases per day"
                              type="number"
                              {...register('max_cases_per_day')}
                            />
                          </Grid>
                        </Grid>
                        <FormControl component="fieldset" className={`${classes.fieldset}`}>
                          <Grid container spacing={2}>
                            <Grid item xs={8}>
                              <h3 className={classes.lableViewMrg}> Procedure Codes</h3>
                            </Grid>
                            <Grid item xs={4} className={classes.mT10}>
                              <Button color="primary" onClick={addQcComponent} endIcon={<AddCircleIcon />}>
                                Add
                              </Button>
                            </Grid>
                          </Grid>
                          <FormGroup row>
                            {userQcSettings.map((val, i) => (
                              // eslint-disable-next-line react/jsx-key
                              <Grid key={i} container spacing={2} className={classes.mT0}>
                                <Grid item xs={4}>
                                  <Autocomplete
                                    id="combo-box-demo"
                                    key={`qcSettingsCode${i}`}
                                    value={dataProcedurecode(val?.procedure_code_id)}
                                    options={procedureCode}
                                    onChange={(e, val) => onCodeChange(val, i)}
                                    renderInput={(params) => (
                                      <TextField {...params} required label="Procedure Code" size="small" />
                                    )}
                                  />
                                  {displayProcessCodeError && !val?.procedure_code_id && (
                                    <p className={classes.error}>Please select the Procedure Code</p>
                                  )}
                                </Grid>
                                <Grid item xs={4}>
                                  <TextField
                                    className={classes.customLableTxt}
                                    required
                                    style={{ width: '100%' }}
                                    InputLabelProps={{
                                      style: { border: '1px solid red !important' },
                                      shrink: true,
                                    }}
                                    defaultValue="1"
                                    inputProps={{
                                      style: {
                                        padding: 10,
                                      },
                                      min: 1,
                                      max: 100,
                                    }}
                                    onKeyPress={(e) => {
                                      if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault()
                                      }
                                    }}
                                    value={val?.qc_percentage || ''}
                                    onChange={(e) => onQcChange(e.target.value, i)}
                                    key={`qcSettingsPercentage${i}`}
                                    id="outlined-basic"
                                    label="QC allocation percentage"
                                    type="number"
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <Button
                                    color="primary"
                                    key={`remove${i}`}
                                    onClick={(e) => removeQcComponent(e, i)}
                                    disabled={qcCount === 1}
                                    endIcon={<ClearIcon />}
                                  >
                                    Remove
                                  </Button>
                                </Grid>
                              </Grid>
                            ))}
                            {isDuplicatePc && <p className={classes.error}>Please remove duplicate Procedure Code</p>}
                          </FormGroup>
                        </FormControl>
                      </>
                    )}
                    {isShow && (
                      <>
                        <Grid container spacing={2} className={classes.mp0}>
                          <h3 className={classes.lableViewMrg}>Qualifications</h3>
                          <Grid item xs={12} className={classes.mpTB0}>
                            <FormControlLabel
                              control={<Checkbox checked={performQc} onChange={handlePerformQc} color="primary" />}
                              {...register('qc_allowed')}
                              className={classes.dBlock}
                              label="User is qualified to perform QC."
                              labelPlacement="end"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox checked={priorityCases} onChange={handlePriorityCases} color="primary" />
                              }
                              {...register('priority_allowed')}
                              className={classes.dBlock}
                              label="User is qualified to accept Priority and/or Hand Hold cases."
                              labelPlacement="end"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox checked={medicareCases} onChange={handleMedicareCases} color="primary" />
                              }
                              {...register('mc_allowed')}
                              className={classes.dBlock}
                              label="User is qualified to process Medicare cases"
                              labelPlacement="end"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={multipleClassifiers}
                                  onChange={handleMultipleclassifiers}
                                  color="primary"
                                />
                              }
                              {...register('multiple_classifiers_allowed')}
                              className={classes.dBlock}
                              label="User is qualified to view multiple classifiers"
                              labelPlacement="end"
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                    {isShow && (
                      <>
                        <Grid container spacing={2} className={classes.mp0}>
                          <Grid item xs={12} className={classes.mB10}>
                            <h3 className={classes.lableViewMrg}>LabOS login details</h3>
                          </Grid>
                          <Grid item xs={2} className={`${classes.mpTB0}, ${classes.mB80}`}>
                            <TextField
                              className={classes.customLableTxt}
                              style={{ width: '100%' }}
                              InputLabelProps={{
                                style: { border: '1px solid red !important' },
                                shrink: true,
                              }}
                              inputProps={{
                                style: {
                                  padding: 10,
                                },
                                min: 1,
                              }}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault()
                                }
                              }}
                              id="outlined-basic"
                              label="User Code"
                              type="number"
                              {...register('lab_os_user_code')}
                            />
                          </Grid>
                          <Grid item xs={2} className={classes.mpTB0}>
                            <TextField
                              className={classes.customLableTxt}
                              style={{ width: '100%' }}
                              InputLabelProps={{
                                style: { border: '1px solid red !important' },
                                shrink: true,
                              }}
                              inputProps={{
                                style: {
                                  padding: 10,
                                },
                              }}
                              id="outlined-basic"
                              label="Login ID"
                              type="text"
                              {...register('lab_os_login_id')}
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </div>
                )}
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions className={classes.editAcitonBtn}>
            <Button
              className={!disableButton || !isShow ? classes.btn : classes.btn2}
              type="submit"
              color="primary"
              disabled={disableButton && isShow}
              endIcon={<CheckIcon />}
            >
              Submit
            </Button>
            <Button variant="outlined" onClick={(e) => onCancel(e)} endIcon={<ClearIcon />}>
              Close
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  )
}
